import React, { useState } from 'react';
import { UserPlus } from 'lucide-react';
import { adminApi } from '../services/api';
import Alert from '../components/Alert';

interface AccountResponse {
    id: string;
    name: string;
    number: number;
    createdAt: string;
    updatedAt: string;
}

const AdminAccountCreation: React.FC = () => {
    const [name, setName] = useState<string>('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [createdAccount, setCreatedAccount] = useState<AccountResponse | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);
        setCreatedAccount(null);

        if (!name.trim()) {
            setError('O nome é obrigatório.');
            return;
        }

        try {
            setSubmitting(true);
            const response = await adminApi.createAccount({ name: name.trim() });
            setCreatedAccount(response.data);
            setSuccess('Conta criada com sucesso!');
            setName('');
        } catch (e: any) {
            const friendly = e?.response?.data?.translatedMessage || 'Falha ao criar a conta.';
            setError(friendly);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-3">
                <div className="p-3 bg-primary-100 rounded-lg">
                    <UserPlus className="h-6 w-6 text-primary-600" />
                </div>
                <div>
                    <h1 className="text-2xl font-bold text-secondary-900">Cadastrar Nova Conta</h1>
                    <p className="text-secondary-600">Crie uma nova conta de sistema</p>
                </div>
            </div>

            {error && (<Alert type="error" title="Não foi possível concluir" message={error} />)}
            {success && createdAccount && (
                <Alert 
                    type="success" 
                    title="Conta criada com sucesso!" 
                    message={
                        <div className="space-y-2">
                            <p>Conta criada com sucesso. Detalhes:</p>
                            <div className="bg-white rounded-lg p-3 border border-green-200 mt-2">
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                    <div>
                                        <span className="font-medium text-secondary-700">Nome:</span>
                                        <span className="ml-2 text-secondary-900">{createdAccount.name}</span>
                                    </div>
                                    <div>
                                        <span className="font-medium text-secondary-700">Número:</span>
                                        <span className="ml-2 text-secondary-900">{createdAccount.number}</span>
                                    </div>
                                    <div>
                                        <span className="font-medium text-secondary-700">ID:</span>
                                        <span className="ml-2 text-secondary-900 font-mono text-xs">{createdAccount.id}</span>
                                    </div>
                                    <div>
                                        <span className="font-medium text-secondary-700">Criado em:</span>
                                        <span className="ml-2 text-secondary-900">
                                            {new Date(createdAccount.createdAt).toLocaleString('pt-BR')}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    } 
                />
            )}

            <div className="bg-white p-6 rounded-lg border border-secondary-200 max-w-2xl">
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-1">
                            Nome *
                        </label>
                        <input
                            type="text"
                            className="input-field w-full"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Nome da conta"
                            required
                            autoFocus
                        />
                        <p className="mt-1 text-xs text-secondary-500">
                            O nome será usado para identificar a conta de sistema
                        </p>
                    </div>

                    <div className="flex justify-end space-x-3 pt-4">
                        <button
                            type="submit"
                            className="btn-primary flex items-center gap-2"
                            disabled={submitting}
                        >
                            <UserPlus className="w-4 h-4" />
                            {submitting ? 'Criando...' : 'Cadastrar Conta'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AdminAccountCreation;

